import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useUpdateNavReconRiskDataMeta } from '../../../../../api/rs-hg/nav-recon/hooks/mutations';
import { NavReconClientAuditStatus } from '../../../../../api/rs-hg/nav-recon/types';
import { NAV_RECON_RISK_MONITOR } from '../pipelineMetadata';
import {
  formatNavReconAutoApproveWindow,
  formatNavReconElapsedTime,
} from '../navReconTimerUtils';
import {
  UciluxAuditorNavReconWriteNotice,
  blockUciluxAuditorNavReconAction,
  useIsUciluxAuditorReadOnly,
} from '../../../../../temporary/uciluxAuditorReadOnly/uciluxAuditorReadOnly.hack';

/** @deprecated Import from `navReconTimerUtils` instead. */
export { NAV_RECON_AUDIT_AUTO_APPROVE_AFTER_MS } from '../navReconTimerUtils';

const NAV_RECON_AUDIT_COMMENT_MAX_LENGTH = 300;

interface Props {
  fundName: string;
  positionDate: string;
  snapshotTimestamp: string | null;
  status: NavReconClientAuditStatus;
  clientComment: string | null;
  clientAuditByName: string | null;
}

type DisplayStatus = 'APPROVED' | 'REJECTED' | 'PENDING';

const statusClasses: Record<DisplayStatus, string> = {
  APPROVED: 'border-green-300 bg-green-50 text-green-900',
  REJECTED: 'border-red-300 bg-red-50 text-red-900',
  PENDING: 'border-amber-300 bg-amber-50 text-amber-900',
};

const getDisplayStatus = (status: NavReconClientAuditStatus): DisplayStatus => {
  const normalized = status?.toString().toLowerCase();
  if (normalized === 'approved') return 'APPROVED';
  if (normalized === 'rejected') return 'REJECTED';
  return 'PENDING';
};

const getTooltipText = (snapshotTimestamp: string | null) => {
  if (!snapshotTimestamp) {
    return 'No risk monitor snapshot.';
  }

  return 'Review audit status.';
};

const formatSnapshotTime = (snapshotTimestamp: string | null) => {
  if (!snapshotTimestamp) return 'No snapshot available';
  const parsed = new Date(snapshotTimestamp);
  if (Number.isNaN(parsed.getTime())) return snapshotTimestamp;

  return parsed.toLocaleString();
};

const TOOLTIP_GAP_PX = 8;
const VIEWPORT_EDGE_PADDING = 8;

const positionTooltipAboveAnchor = (
  anchor: HTMLElement,
  tooltip: HTMLElement,
): { left: number; top: number } => {
  const anchorRect = anchor.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const centerX = anchorRect.left + anchorRect.width / 2;
  let left = centerX - tooltipRect.width / 2;
  const minLeft = VIEWPORT_EDGE_PADDING;
  const maxLeft = Math.max(
    minLeft,
    window.innerWidth - VIEWPORT_EDGE_PADDING - tooltipRect.width,
  );
  if (left < minLeft) left = minLeft;
  if (left > maxLeft) left = maxLeft;
  const top = anchorRect.top - TOOLTIP_GAP_PX - tooltipRect.height;
  return { left, top };
};

type AuditModalActionButtonProps = {
  label: string;
  pendingLabel: string;
  variant: 'approve' | 'reject';
  disabled: boolean;
  disabledReason: string;
  isPending: boolean;
  onClick: () => void;
};

const auditModalActionVariantClass: Record<
  AuditModalActionButtonProps['variant'],
  string
> = {
  approve:
    'bg-emerald-600 text-white hover:bg-emerald-700 focus-visible:ring-emerald-500',
  reject: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500',
};

/**
 * Disabled buttons do not receive pointer events; the wrapper span does so
 * hover tooltips still work.
 */
const AuditModalActionButton: React.FC<AuditModalActionButtonProps> = ({
  label,
  pendingLabel,
  variant,
  disabled,
  disabledReason,
  isPending,
  onClick,
}) => {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipCoords, setTipCoords] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const showDisabledTip = () => {
    if (!disabled || !disabledReason) return;
    setTipOpen(true);
  };

  const hideDisabledTip = () => {
    setTipOpen(false);
    setTipCoords(null);
  };

  useLayoutEffect(() => {
    if (!tipOpen || !disabled || !disabledReason) {
      setTipCoords(null);
      return;
    }

    const anchor = wrapRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;

    setTipCoords(positionTooltipAboveAnchor(anchor, tip));
  }, [tipOpen, disabled, disabledReason]);

  const disabledTip =
    tipOpen && disabled && disabledReason ? (
      <div
        ref={tipRef}
        role="tooltip"
        className="pointer-events-none fixed z-[10001] w-max max-w-[min(16rem,calc(100vw-1rem))] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-[0.6875rem] font-medium leading-snug text-gray-600 shadow-sm ring-1 ring-black/5"
        style={
          tipCoords
            ? { left: tipCoords.left, top: tipCoords.top, visibility: 'visible' }
            : { left: -9999, top: -9999, visibility: 'hidden' }
        }
      >
        {disabledReason}
      </div>
    ) : null;

  return (
    <>
      <span
        ref={wrapRef}
        className={disabled ? 'inline-flex cursor-not-allowed' : 'inline-flex'}
        onMouseEnter={showDisabledTip}
        onMouseLeave={hideDisabledTip}
        onFocus={showDisabledTip}
        onBlur={hideDisabledTip}
        tabIndex={disabled && disabledReason ? 0 : undefined}
      >
        <button
          type="button"
          className={`rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 ${auditModalActionVariantClass[variant]}`}
          onClick={onClick}
          disabled={disabled}
          aria-disabled={disabled}
        >
          {isPending ? pendingLabel : label}
        </button>
      </span>
      {typeof document !== 'undefined' && disabledTip
        ? createPortal(disabledTip, document.body)
        : disabledTip}
    </>
  );
};

const formatPositionDate = (date: string) => {
  if (!date?.trim()) return '—';
  const datePart = date.trim().split('T')[0] ?? date.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;

  const parsed = new Date(`${datePart}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date.trim();

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const NavReconAuditStatusCell: React.FC<Props> = ({
  fundName,
  positionDate,
  snapshotTimestamp,
  status,
  clientComment,
  clientAuditByName,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipCoords, setTooltipCoords] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const mutation = useUpdateNavReconRiskDataMeta();
  // TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly. Delete with that
  // folder when write access is a resource.
  const isUciluxAuditorReadOnly = useIsUciluxAuditorReadOnly();

  const displayStatus = useMemo(() => getDisplayStatus(status), [status]);
  const isPending = displayStatus === 'PENDING';
  const canActOnAudit = isPending && Boolean(snapshotTimestamp);
  const trimmedComment = comment.trim();
  const commentLength = comment.length;
  const auditUserName = clientAuditByName || 'Unknown user';

  const closeModalAfterSuccess = () => {
    setModalOpen(false);
    setComment('');
  };

  const auditActionDisabled = !trimmedComment || mutation.isPending;
  const auditActionDisabledReason = mutation.isPending
    ? 'Saving audit decision…'
    : !trimmedComment
      ? 'Enter a comment before you can approve or reject.'
      : '';

  const handleAuditAction = (
    client_audit_status: 'approved' | 'rejected',
  ) => {
    if (isUciluxAuditorReadOnly || !snapshotTimestamp || !trimmedComment) return;

    mutation.mutate(
      {
        fund_name: fundName,
        position_date: positionDate,
        rs_data_name: NAV_RECON_RISK_MONITOR,
        snapshot_timestamp: snapshotTimestamp,
        client_audit_status,
        client_comment: trimmedComment,
      },
      { onSuccess: closeModalAfterSuccess },
    );
  };

  const showTooltip = () => {
    if (modalOpen) return;
    setTooltipOpen(true);
  };

  const hideTooltip = () => {
    setTooltipOpen(false);
    setTooltipCoords(null);
  };

  useEffect(() => {
    if (modalOpen) {
      setTooltipOpen(false);
      setTooltipCoords(null);
    }
  }, [modalOpen]);

  useLayoutEffect(() => {
    if (!tooltipOpen || modalOpen) {
      setTooltipCoords(null);
      return;
    }

    const button = buttonRef.current;
    const tooltip = tooltipRef.current;
    if (!button || !tooltip) return;

    const br = button.getBoundingClientRect();
    const tr = tooltip.getBoundingClientRect();

    const centerX = br.left + br.width / 2;
    let left = centerX - tr.width / 2;
    const minLeft = VIEWPORT_EDGE_PADDING;
    const maxLeft = Math.max(
      minLeft,
      window.innerWidth - VIEWPORT_EDGE_PADDING - tr.width,
    );
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    const top = br.top - TOOLTIP_GAP_PX - tr.height;

    setTooltipCoords({ left, top });
  }, [tooltipOpen, modalOpen]);

  const modal = modalOpen ? (
    <div
      className="fixed inset-0 z-[9999] w-screen overflow-y-auto"
      role="presentation"
      onMouseDown={() => {
        if (!mutation.isPending) setModalOpen(false);
      }}
    >
      <div className="fixed inset-0 bg-black/25" />
      <div className="flex min-h-full items-center justify-center p-4 pt-16">
        <div
          className="relative z-10 flex w-full max-w-lg flex-col rounded-2xl bg-white p-5 text-left align-middle shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nav-recon-audit-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
                NAV reconciliation
              </p>
              <h2
                id="nav-recon-audit-title"
                className="m-0 mt-1 text-xl font-black text-mainblue"
              >
                Audit Status
              </h2>
            </div>
            <span
              className={`mt-1 inline-block rounded-md border px-2.5 py-1 text-xs font-bold ${statusClasses[displayStatus]}`}
            >
              {displayStatus}
            </span>
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/70 p-3">
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Fund
                </dt>
                <dd className="mt-1.5 font-semibold text-gray-900">
                  {fundName}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Position date
                </dt>
                <dd className="mt-1.5 font-semibold text-gray-900">
                  {formatPositionDate(positionDate)}
                </dd>
              </div>
            </dl>
          </div>

          {isPending ? (
            <>
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="m-0 font-semibold">Pending review</p>
                  <p className="m-0 text-xs text-amber-900/80">
                    Auto-approves after {formatNavReconAutoApproveWindow()}
                  </p>
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="font-medium text-amber-800/75">Snapshot</dt>
                    <dd className="m-0 mt-0.5 font-semibold">
                      {formatSnapshotTime(snapshotTimestamp)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-amber-800/75">Elapsed</dt>
                    <dd className="m-0 mt-0.5 font-semibold">
                      {formatNavReconElapsedTime(snapshotTimestamp)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly. */}
              <UciluxAuditorNavReconWriteNotice />
              {!isUciluxAuditorReadOnly && canActOnAudit ? (
                <>
                  <label
                    htmlFor="nav-recon-audit-comment"
                    className="mt-4 block text-sm font-semibold text-gray-800"
                  >
                    Comment
                  </label>
                  <p className="m-0 mt-1 text-xs text-gray-600">
                    A comment is required to approve or reject manually.
                  </p>
                  <textarea
                    id="nav-recon-audit-comment"
                    autoFocus
                    maxLength={NAV_RECON_AUDIT_COMMENT_MAX_LENGTH}
                    placeholder="Eg. 'Alert reviewed internally, variance within tolerance and signed off'"
                    className="mt-2 min-h-[6rem] w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-mainblue focus:ring-2 focus:ring-blue-200"
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                  />
                  <p className="m-0 mt-1 text-right text-xs text-gray-500">
                    {commentLength}/{NAV_RECON_AUDIT_COMMENT_MAX_LENGTH}
                  </p>
                </>
              ) : !isUciluxAuditorReadOnly ? (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700 shadow-sm">
                  Approve and reject are unavailable because no risk monitor
                  snapshot timestamp is available.
                </div>
              ) : null}
            </>
          ) : null}

          {displayStatus === 'APPROVED' ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-emerald-800/80">
                Audit decision
              </p>
              <p className="m-0 mt-2 text-emerald-900/90">
                Approved by{' '}
                <span className="font-semibold">{auditUserName}</span>.
              </p>
              {clientComment ? (
                <p className="m-0 mt-3 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-emerald-100 bg-white/80 px-3 py-2 text-[0.8125rem] leading-relaxed text-gray-800">
                  {clientComment}
                </p>
              ) : null}
            </div>
          ) : null}

          {displayStatus === 'REJECTED' ? (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950">
              <p className="m-0 text-xs font-semibold uppercase tracking-wide text-red-800/80">
                Audit decision
              </p>
              <p className="m-0 mt-2 text-red-900/90">
                Rejected by{' '}
                <span className="font-semibold">{auditUserName}</span> with
                comment;
              </p>
              <p className="m-0 mt-3 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-red-100 bg-white/80 px-3 py-2 text-[0.8125rem] leading-relaxed text-gray-800">
                {clientComment || 'No rejection comment was provided.'}
              </p>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => setModalOpen(false)}
              disabled={mutation.isPending}
            >
              Close
            </button>
            {isPending && canActOnAudit ? (
              <>
                {/* TEMPORARY HACK — src/temporary/uciluxAuditorReadOnly.
                    blockUciluxAuditorNavReconAction is a no-op for every other user. */}
                <AuditModalActionButton
                  label="Approve"
                  pendingLabel="Saving…"
                  variant="approve"
                  {...blockUciluxAuditorNavReconAction(
                    isUciluxAuditorReadOnly,
                    auditActionDisabled,
                    auditActionDisabledReason,
                  )}
                  isPending={mutation.isPending}
                  onClick={() => handleAuditAction('approved')}
                />
                <AuditModalActionButton
                  label="Reject"
                  pendingLabel="Saving…"
                  variant="reject"
                  {...blockUciluxAuditorNavReconAction(
                    isUciluxAuditorReadOnly,
                    auditActionDisabled,
                    auditActionDisabledReason,
                  )}
                  isPending={mutation.isPending}
                  onClick={() => handleAuditAction('rejected')}
                />
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const tooltip =
    tooltipOpen && !modalOpen ? (
      <div
        ref={tooltipRef}
        className="pointer-events-none fixed z-[10000] w-max max-w-[min(12rem,calc(100vw-1rem))] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-[0.6875rem] font-medium leading-snug text-gray-600 shadow-sm ring-1 ring-black/5"
        style={
          tooltipCoords
            ? {
              left: tooltipCoords.left,
              top: tooltipCoords.top,
              visibility: 'visible' as const,
            }
            : {
              left: -9999,
              top: -9999,
              visibility: 'hidden' as const,
            }
        }
      >
        {getTooltipText(snapshotTimestamp)}
      </div>
    ) : null;

  return (
    <>
      <span className="relative inline-flex justify-center">
        <button
          ref={buttonRef}
          type="button"
          className={`inline-block min-w-[6.75rem] cursor-pointer rounded-md border px-2 py-1 text-center text-xs font-bold hover:brightness-95 ${statusClasses[displayStatus]}`}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          onClick={() => {
            hideTooltip();
            setModalOpen(true);
          }}
        >
          {displayStatus}
        </button>
      </span>
      {typeof document !== 'undefined' && tooltip
        ? createPortal(tooltip, document.body)
        : tooltip}
      {typeof document !== 'undefined' && modal
        ? createPortal(modal, document.body)
        : modal}
    </>
  );
};

export default NavReconAuditStatusCell;
